import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    allowEmptyOption: Boolean,
    attachedToBody: Boolean,
    fixModalSelection: Boolean,
    dropdownHeader: String,
    maxItems: Number,
    create: Boolean,
    createFilter: String
  }

  initialize() {
    this.selectizeOptions = {}
  }

  connect() {
    this.initializeSelectize()

    // Turbo does not order disconnect() against its snapshot clone —
    // View#cacheSnapshot awaits an event loop tick before clone() and
    // Visit#loadResponse doesn't await cacheSnapshot() — so tearing down only in
    // disconnect() races the cache and can snapshot a mangled select.
    // turbo:before-cache fires synchronously before the clone, making teardown
    // there deterministic.
    this.teardownBeforeCache = () => this.destroySelectize()
    document.addEventListener("turbo:before-cache", this.teardownBeforeCache)

    this.element["select"] = this

    if (this.element.multiple && this.element.name && !this.element.name.endsWith("[]")) {
      this.element.name += "[]"
    }

    if (this.element.hasAttribute("readonly")) {
      this.selectize.disable()
      this.element.removeAttribute("disabled")
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.teardownBeforeCache)
    this.destroySelectize()
  }

  initializeSelectize() {
    const options = {
      ...this.defaultSelectizeOptions,
      ...this.selectizeOptions
    }

    $(this.element).selectize(options)

    // Selectize copies classes from the original <select> to .selectize-control
    // and .selectize-dropdown — remove cocoon-field from those.
    const wrapper = this.element.nextElementSibling
    if (wrapper) wrapper.classList.remove("cocoon-field")
    const dropdown = wrapper?.querySelector(".selectize-dropdown")
    if (dropdown) dropdown.classList.remove("cocoon-field")
  }

  destroySelectize() {
    if (!this.selectize) return

    // Capture the value before tearing down: selectize.destroy() re-appends the
    // original <option> nodes it detached on setup, which leaves the browser's
    // selectedness on an arbitrary option.
    const values = [this.selectize.getValue()].flat()

    this.selectize.destroy()

    // Turbo clones this element into its snapshot cache *after* we run — its
    // View#cacheSnapshot awaits an event loop tick before clone(), and
    // Visit#loadResponse doesn't await cacheSnapshot() — and clone() copies
    // over `selectedOptions`. So whatever selectedness we leave behind here is
    // what the browser Back button restores.
    Array.from(this.element.options).forEach(option => {
      option.selected = values.includes(option.value)
    })
  }

  onChange() {
    this.dispatch("change", { prefix: false })
  }

  onInitialize() {
    this.dispatch("initialized")
  }

  dropdownOpened($dropdown) {
    if (this.attachedToBodyValue) $dropdown[0].style.zIndex = 1060

    if (this.fixModalSelectionValue) {
      document.querySelectorAll(".modal").forEach((modal) => {
        const modalData = $(modal).data("bs.modal")
        if (!modalData) return

        modalData.previousValues = {}
        modalData.previousValues.backdrop = modalData.options.backdrop
        modalData.previousValues.keyboard = modalData.options.keyboard
        modalData.options.backdrop = "static"
        modalData.options.keyboard = false
      })
    }
  }

  dropdownClosed($dropdown) {
    if (this.fixModalSelectionValue) {
      document.querySelectorAll(".modal").forEach((modal) => {
        const modalData = $(modal).data("bs.modal")
        if (!modalData || !modalData.previousValues) return

        // this is a horrendous hack, but Bootstrap 3 gave me no other choice
        setTimeout(
          () => {
            modalData.options.backdrop = modalData.previousValues.backdrop
            modalData.options.keyboard = modalData.previousValues.keyboard
          },
          120
        )
      })
    }
  }

  get defaultSelectizeOptions() {
    const defaultAttributes = ["text", "value", "disabled", "$order"]

    const opts = {
      onInitialize: this.onInitialize.bind(this),
      onChange: this.onChange.bind(this),
      onDropdownOpen: this.dropdownOpened.bind(this),
      onDropdownClose: this.dropdownClosed.bind(this),
      allowEmptyOption: this.allowEmptyOptionValue,
      create: this.createValue ? this.handleCreate.bind(this) : null,
      createFilter: this.createFilterValue,
      plugins: [],
      render: {
        option: function (data, escape) {
          const dataAttributes = Reflect.ownKeys(data).filter(element => !defaultAttributes.includes(element))
          const dataAttributesString = dataAttributes.map(attribute => {
            return `data-${attribute}="${escape(data[attribute])}"`
          }).join(" ")
          return `<div class='option' ${escape(dataAttributesString)}>${escape(data.text)}</div>`
        }
      }
    }

    if (this.hasMaxItemsValue) opts.maxItems = this.maxItemsValue

    if (this.element.multiple && this.maxItemsValue != 1) opts.plugins.push("remove_button")

    if (this.dropdownHeaderValue.length) {
      opts.plugins.push({
        name: "dropdown_header",
        options: {
          title: this.dropdownHeaderValue,
          closeClass: "hidden"
        }
      })
    }

    if (this.attachedToBodyValue) opts.dropdownParent = "body"

    return opts
  }

  handleCreate(input, callback) {
    const event = this.dispatch("create", { detail: { input, callback } })
    if (!event.defaultPrevented) { // there was no create handler
      return { value: input, text: input }
    }
  }

  setValue(value) {
    this.selectize.setValue(value)
  }

  replaceOptions(options) {
    this.clearOptions()
    this.selectize.addOption(options)
    this.selectize.refreshOptions(false)
  }

  hideOptions(values) {
    this.selectize.refreshOptions(false)
    this.optionElements.forEach(option => {
      option.hidden = values.includes(option.dataset.value)
    })
  }

  clearOptions() {
    this.clear()
    this.selectize.clearOptions()
  }

  clear() {
    this.selectize.clear()
  }

  disable() {
    this.selectize.disable()
  }

  enable() {
    this.selectize.enable()
  }

  get selectize() {
    return this.element.selectize
  }

  get optionElements() {
    return Array.from(this.element.nextElementSibling.querySelectorAll(".option"))
  }
}
